import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getClubWaivers,
  grantWaiver,
  revokeWaiver,
  formatMoney,
  type FeeWaiver,
} from "../../services/payments";
import { getClubAthletes, getProducts, type ClubAthlete, type Product } from "../../services/api";
import { formatDate } from "../../lib/dateFormat";
import { AdminCard } from "../admin/AdminCard";
import DateInput from "../DateInput";

/**
 * Burse și scutiri — the money a club chooses not to ask for.
 *
 * Shown with what each one has cost, because that is the number a committee
 * asks about at the end of a season and the one nobody can reconstruct
 * afterwards. Revoking keeps the row: a waiver that paid for a child's season
 * is part of the club's record of what it gave, and so is the person who
 * approved it.
 */

const MIN_REASON = 4;

const todayISO = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function ClubWaivers({ clubId }: { clubId: string }) {
  const { t } = useTranslation();

  const [waivers, setWaivers] = useState<FeeWaiver[]>([]);
  const [athletes, setAthletes] = useState<ClubAthlete[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [athleteId, setAthleteId] = useState("");
  const [productId, setProductId] = useState("");
  const [kind, setKind] = useState<"full" | "percent" | "amount">("full");
  const [percent, setPercent] = useState("50");
  const [amount, setAmount] = useState("");
  const [startsOn, setStartsOn] = useState(todayISO);
  const [endsOn, setEndsOn] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setWaivers(await getClubWaivers(clubId));
    } catch (e) {
      console.error("Failed to load waivers:", e);
      setError(true);
    }
    setLoading(false);
  }, [clubId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getClubAthletes(clubId).then(setAthletes).catch(() => setAthletes([]));
    getProducts(clubId).then(setProducts).catch(() => setProducts([]));
  }, [clubId]);

  const visible = useMemo(
    () => waivers.filter((w) => (showRevoked ? true : !w.revoked_at)),
    [waivers, showRevoked]
  );
  const revokedCount = useMemo(() => waivers.filter((w) => w.revoked_at).length, [waivers]);
  const givenCents = useMemo(() => waivers.reduce((s, w) => s + w.applied_cents, 0), [waivers]);

  const resetForm = () => {
    setAthleteId("");
    setProductId("");
    setKind("full");
    setPercent("50");
    setAmount("");
    setStartsOn(todayISO());
    setEndsOn("");
    setReason("");
    setFormError(null);
  };

  const submit = async () => {
    const trimmed = reason.trim();
    if (!athleteId) {
      setFormError(t("waivers.needAthlete", "Alege sportivul."));
      return;
    }
    if (trimmed.length < MIN_REASON) {
      setFormError(t("waivers.needReason", "Scrie un motiv — rămâne în evidența clubului."));
      return;
    }

    let percentValue: number | null = null;
    let amountCents: number | null = null;
    if (kind === "percent") {
      percentValue = Math.round(Number(percent));
      if (!Number.isFinite(percentValue) || percentValue < 1 || percentValue > 100) {
        setFormError(t("waivers.badPercent", "Procentul trebuie să fie între 1 și 100."));
        return;
      }
    }
    if (kind === "amount") {
      amountCents = Math.round(Number(amount.replace(",", ".")) * 100);
      if (!Number.isFinite(amountCents) || amountCents < 1) {
        setFormError(t("waivers.badAmount", "Sumă invalidă."));
        return;
      }
    }

    setSubmitting(true);
    setFormError(null);
    try {
      const res = await grantWaiver(clubId, {
        athleteId,
        productId: productId || null,
        kind,
        percent: percentValue,
        amountCents,
        startsOn,
        endsOn: endsOn || null,
        reason: trimmed,
      });
      setOpen(false);
      resetForm();
      setNotice(
        res.repriced > 0
          ? t("waivers.granted", "Scutire acordată. {{count}} perioade neachitate au fost recalculate.", {
              count: res.repriced,
            })
          : t("waivers.grantedNoChange", "Scutire acordată. Se aplică perioadelor următoare.")
      );
      await load();
    } catch (e: any) {
      setFormError(e?.response?.data?.error || t("waivers.failed", "Nu am putut acorda scutirea."));
    }
    setSubmitting(false);
  };

  const revoke = async (w: FeeWaiver) => {
    const name = w.athlete_name || t("waivers.athlete", "sportiv");
    if (
      !window.confirm(
        t(
          "waivers.confirmRevoke",
          "Retragi scutirea pentru {{name}}? Perioadele neachitate revin la prețul întreg. Ce a fost deja scutit rămâne scutit.",
          { name }
        )
      )
    )
      return;

    try {
      const res = await revokeWaiver(clubId, w.id);
      setNotice(
        t("waivers.revoked", "Scutire retrasă. {{count}} perioade au revenit la prețul întreg.", {
          count: res.repriced,
        })
      );
      await load();
    } catch (e: any) {
      setNotice(e?.response?.data?.error || t("waivers.revokeFailed", "Nu am putut retrage scutirea."));
    }
  };

  const describe = (w: FeeWaiver) => {
    if (w.kind === "full") return t("waivers.full", "Scutire integrală");
    if (w.kind === "percent") return t("waivers.percent", "{{percent}}% reducere", { percent: w.percent || 0 });
    return t("waivers.amount", "{{amount}} / perioadă", {
      amount: formatMoney(w.amount_cents || 0),
    });
  };

  const inputCls =
    "w-full px-3 py-2 text-base bg-white border rounded-lg border-slate-300 lg:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";

  return (
    <div className="space-y-6">
      <AdminCard>
        <div className="flex flex-wrap items-start justify-between gap-4 p-4">
          <div>
            <h3 className="font-bold text-slate-800">{t("waivers.title", "Burse și scutiri")}</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-prose">
              {t(
                "waivers.hint",
                "O scutire e o instrucțiune permanentă: toate perioadele generate de acum înainte o moștenesc, fără să își mai amintească nimeni. Nu se cumulează cu reducerea pentru frați — scutirea decide."
              )}
            </p>
            {givenCents > 0 && (
              <p className="mt-2 text-sm font-semibold text-slate-700">
                {t("waivers.totalGiven", "Acordat până acum: {{amount}}", { amount: formatMoney(givenCents) })}
              </p>
            )}
          </div>
          <button
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
            className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            {t("waivers.add", "Acordă o scutire")}
          </button>
        </div>
      </AdminCard>

      {notice && (
        <div className="flex items-start gap-3 p-4 border rounded-2xl border-indigo-200 bg-indigo-50">
          <p className="flex-1 text-sm text-indigo-900">{notice}</p>
          <button onClick={() => setNotice(null)} className="text-sm font-semibold text-indigo-700">
            {t("common.close", "Închide")}
          </button>
        </div>
      )}

      <AdminCard>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 rounded-full border-indigo-600 animate-spin border-t-transparent" />
          </div>
        ) : error ? (
          <div className="py-16 text-center">
            <p className="text-slate-500">{t("error.loadFailed", "Nu s-au putut încărca datele")}</p>
            <button onClick={load} className="mt-3 text-sm font-semibold text-indigo-600">
              {t("common.retry", "Reîncearcă")}
            </button>
          </div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center">
            <p className="font-medium text-slate-700">{t("waivers.emptyTitle", "Nicio scutire")}</p>
            <p className="mt-1 text-sm text-slate-500">
              {t("waivers.emptyHint", "Aici apar bursele și scutirile acordate de club, cu ce a costat fiecare.")}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 font-semibold text-slate-600">{t("waivers.athlete", "Sportiv")}</th>
                  <th className="px-4 py-3 font-semibold text-slate-600">{t("waivers.what", "Scutire")}</th>
                  <th className="px-4 py-3 font-semibold text-slate-600">{t("waivers.period", "Perioadă")}</th>
                  <th className="px-4 py-3 font-semibold text-right text-slate-600">
                    {t("waivers.applied", "Acordat")}
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600">{t("waivers.reason", "Motiv")}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {visible.map((w) => (
                  <tr
                    key={w.id}
                    className={`border-b border-slate-100 last:border-0 ${w.revoked_at ? "opacity-60" : "hover:bg-slate-50/60"}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{w.athlete_name || "—"}</div>
                      <div className="text-[11px] text-slate-400">
                        {w.product_name || t("waivers.allProducts", "toate serviciile clubului")}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {describe(w)}
                      {w.revoked_at && (
                        <span className="block text-[11px] font-semibold text-rose-600">
                          {t("waivers.revokedOn", "retrasă {{date}}", { date: formatDate(w.revoked_at) })}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 tabular-nums">
                      {formatDate(w.starts_on)}
                      {" → "}
                      {w.ends_on ? formatDate(w.ends_on) : t("waivers.openEnded", "nelimitat")}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-800">
                      {w.applied_cents > 0 ? (
                        <>
                          <span className="font-semibold">{formatMoney(w.applied_cents)}</span>
                          <span className="block text-[11px] text-slate-400">
                            {t("waivers.periods", "{{count}} perioade", { count: w.applied_periods })}
                          </span>
                        </>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs">
                      <span className="block truncate" title={w.reason}>
                        {w.reason}
                      </span>
                      {w.granted_by_name && (
                        <span className="block text-[11px] text-slate-400">{w.granted_by_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!w.revoked_at && (
                        <button
                          onClick={() => revoke(w)}
                          className="px-3 py-1.5 text-xs font-semibold border rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50"
                        >
                          {t("waivers.revoke", "Retrage")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {revokedCount > 0 && (
          <div className="px-4 py-3 border-t border-slate-100">
            <button
              onClick={() => setShowRevoked((v) => !v)}
              className="text-sm font-semibold text-slate-500 hover:text-slate-700"
            >
              {showRevoked
                ? t("waivers.hideRevoked", "Ascunde scutirile retrase")
                : t("waivers.showRevoked", "Arată și cele retrase ({{count}})", { count: revokedCount })}
            </button>
          </div>
        )}
      </AdminCard>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-lg p-6 bg-white shadow-xl rounded-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-slate-800">{t("waivers.addTitle", "Acordă o scutire")}</h3>

            <label className="block mt-4 mb-1 text-xs font-semibold text-slate-500">
              {t("waivers.athlete", "Sportiv")} *
            </label>
            <select value={athleteId} onChange={(e) => setAthleteId(e.target.value)} className={inputCls}>
              <option value="">{t("waivers.choose", "Alege...")}</option>
              {athletes.map((a) => (
                <option key={a.user.id} value={a.user.id}>
                  {a.user.full_name || a.user.email}
                </option>
              ))}
            </select>

            <label className="block mt-4 mb-1 text-xs font-semibold text-slate-500">
              {t("waivers.product", "Serviciu")}
            </label>
            <select value={productId} onChange={(e) => setProductId(e.target.value)} className={inputCls}>
              <option value="">{t("waivers.allProducts", "toate serviciile clubului")}</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            <label className="block mt-4 mb-1 text-xs font-semibold text-slate-500">
              {t("waivers.kind", "Cât")}
            </label>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ["full", t("waivers.full", "Scutire integrală")],
                  ["percent", t("waivers.kindPercent", "Procent")],
                  ["amount", t("waivers.kindAmount", "Sumă fixă")],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setKind(id)}
                  className={`px-3 py-2 text-sm font-semibold border rounded-lg transition-colors ${
                    kind === id
                      ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                      : "border-slate-300 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {kind === "percent" && (
              <div className="mt-3">
                <label className="block mb-1 text-xs font-semibold text-slate-500">
                  {t("waivers.percentLabel", "Procent (%)")}
                </label>
                <input
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  inputMode="numeric"
                  className={inputCls}
                />
              </div>
            )}

            {kind === "amount" && (
              <div className="mt-3">
                <label className="block mb-1 text-xs font-semibold text-slate-500">
                  {t("waivers.amountLabel", "Sumă scutită pe perioadă (RON)")}
                </label>
                <input
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  inputMode="decimal"
                  className={inputCls}
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  {t(
                    "waivers.amountHint",
                    "Se scade din fiecare perioadă, nu o singură dată. Nu depășește niciodată prețul perioadei."
                  )}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <label className="block mb-1 text-xs font-semibold text-slate-500">
                  {t("waivers.startsOn", "De la")}
                </label>
                <DateInput value={startsOn} onChange={setStartsOn} className={inputCls} />
              </div>
              <div>
                <label className="block mb-1 text-xs font-semibold text-slate-500">
                  {t("waivers.endsOn", "Până la")}
                </label>
                <DateInput value={endsOn} onChange={setEndsOn} className={inputCls} />
                <p className="mt-1 text-[11px] text-slate-400">
                  {t("waivers.endsOnHint", "Gol = până o retragi.")}
                </p>
              </div>
            </div>

            <label className="block mt-4 mb-1 text-xs font-semibold text-slate-500">
              {t("waivers.reason", "Motiv")} *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder={t("waivers.reasonPlaceholder", "ex. situație socială, decizia comitetului din 3 martie")}
              className={inputCls}
            />
            <p className="mt-1 text-[11px] text-slate-400">
              {t(
                "waivers.reasonWhy",
                "Rămâne în evidența clubului, împreună cu numele celui care a acordat-o. Peste doi ani e singura explicație care mai există."
              )}
            </p>

            {formError && <p className="mt-3 text-sm text-rose-600">{formError}</p>}

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setOpen(false)}
                disabled={submitting}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800 disabled:opacity-50"
              >
                {t("common.cancel", "Anulează")}
              </button>
              <button
                onClick={submit}
                disabled={submitting || !athleteId || reason.trim().length < MIN_REASON}
                className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitting ? t("common.loading", "Se încarcă...") : t("waivers.grant", "Acordă")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
